import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { filter } from 'rxjs'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type BulkCopyJobsFn } from '@/features/jobs/JobStore'
import { Id } from '@/infra/id'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { tapAction } from '@/utils/mobx-util'
import { type BulkActionJobErrorDialogViewModel } from '@/features/jobs/list/components/BulkActionJobErrorDialog/BulkActionJobErrorDialogViewModel'
import type { BulkActionJob } from '@/features/jobs/list/bulk-actions'
import { type BulkCopyJobsSuccessDialogViewModel } from '@/features/jobs/list/components/BulkCopyJobsSuccessDialog/BulkCopyJobsSuccessDialogViewModel'

/**
 * Confirms copying the selected jobs and copies them.
 */
export class BulkCopyJobsDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * View model for the underlying modal.
   */
  readonly modal = new ModalViewModel()

  @observable.ref private accessor _jobs: BulkActionJob[] = []

  private _clonedJobIds = new Set<Id>()

  constructor(
    private readonly _searchableJobStore: SearchableJobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _bulkCopyJobs: BulkCopyJobsFn,
    private readonly _pushJob: (job: SearchableJob) => void,
    private readonly _errorDialogVm: BulkActionJobErrorDialogViewModel,
    private readonly _successDialogVm: BulkCopyJobsSuccessDialogViewModel,
  ) {
    this._lifecycle.manage(
      this._searchableJobStore.jobEvents$.pipe(
        filter(
          (e) =>
            e.type === 'JobUpdated' &&
            Id.some(e.job.id) &&
            this._clonedJobIds.has(e.job.id),
        ),
        tapAction((e) => {
          this._pushJob(e.job)
          this._clonedJobIds.delete(e.job.id)
        }),
      ),
    )
  }

  /**
   * How many jobs are about to be copied.
   */
  @computed
  get jobsCount() {
    return this._jobs.length
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Asks to confirm copying the given jobs.
   */
  @action.bound
  show(jobs: BulkActionJob[]) {
    this._jobs = jobs
    this.modal.show()
  }

  /**
   * Copies the jobs.
   */
  copyJobs = task.resolved(async () => {
    const alert = this._newAlert()
      .show()
      .withState('pending')
      .saying('Copying jobs...')
    const result = await this._bulkCopyJobs(this._jobs.map((j) => j.id))
      .finally(() => alert.dismiss())
      .catch(this._createAlertErrorHandler('Copying the jobs failed'))

    this.modal.close()

    if (result.errors.length > 0) {
      this._errorDialogVm.showErrors(result.errors, this._jobs)
    } else if (result.copiedJobIds.length > 0) {
      this._clonedJobIds = new Set(result.copiedJobIds)
      this._successDialogVm.show(result.copiedJobIds)
    }
  })
}
